import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Package, ShoppingCart, X, ArrowRight, MapPin, Hash } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import api, { productsApi, inventarioApi, fileUrl } from '@/lib/api';
import ProductDetailDrawer from '@/components/products/ProductDetailDrawer';

const STATUS_LABEL = {
  pending:    { label: 'Pendente',   color: 'text-amber-400  bg-amber-400/10'  },
  confirmed:  { label: 'Confirmada', color: 'text-blue-400   bg-blue-400/10'   },
  processing: { label: 'Em processo',color: 'text-violet-400 bg-violet-400/10' },
  shipped:    { label: 'Enviada',    color: 'text-cyan-400   bg-cyan-400/10'   },
  delivered:  { label: 'Entregue',   color: 'text-emerald-400 bg-emerald-400/10'},
  cancelled:  { label: 'Cancelada',  color: 'text-red-400    bg-red-400/10'    },
};

function Highlight({ text = '', query = '' }) {
  if (!query || !text) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <mark className="bg-blue-500/30 text-blue-200 rounded px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </span>
  );
}

function Avatar({ name, size = 7 }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['bg-blue-600','bg-violet-600','bg-emerald-600','bg-amber-600','bg-pink-600','bg-cyan-600'];
  const color  = colors[(name?.charCodeAt(0) || 0) % colors.length];
  return (
    <div className={`w-${size} h-${size} rounded-full ${color} flex items-center justify-center flex-shrink-0 text-white font-semibold text-xs`}>
      {initials}
    </div>
  );
}

const GlobalSearch = () => {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState(null);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [active,  setActive]  = useState(-1);
  const [drawerProduct, setDrawerProduct] = useState(null);
  const [categoryMap,   setCategoryMap]   = useState({});

  const navigate  = useNavigate();
  const ref       = useRef(null);
  const inputRef  = useRef(null);
  const timeoutRef = useRef(null);

  // Ctrl+K / ⌘K to focus
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults(null); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setResults(res.data);
      setOpen(true);
      setActive(-1);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => search(query), 280);
    return () => clearTimeout(timeoutRef.current);
  }, [query, search]);

  // Build flat list of all navigable items for keyboard nav
  const flatItems = results ? [
    ...(results.customers || []).map(c => ({ type: 'customer', ...c })),
    ...(results.products  || []).map(p => ({ type: 'product',  ...p })),
    ...(results.orders    || []).map(o => ({ type: 'order',    ...o })),
  ] : [];

  const goTo = useCallback((path) => {
    navigate(path);
    setQuery('');
    setOpen(false);
    setActive(-1);
  }, [navigate]);

  const openProduct = useCallback(async (p) => {
    setOpen(false);
    setQuery('');
    setDrawerProduct(p); // mostrar imediatamente com dados parciais
    try {
      const [prodRes, catRes] = await Promise.all([
        productsApi.getOne(p.id),
        inventarioApi.getCategories(),
      ]);
      setCategoryMap(Object.fromEntries((catRes.data || []).map(c => [c.name, c])));
      setDrawerProduct(prodRes.data);
    } catch {
      // mantém os dados parciais
    }
  }, []);

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === 'Escape') { setOpen(false); setActive(-1); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(a + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      const item = flatItems[active];
      if (item.type === 'customer') goTo(`/clientes/${item.id}`);
      else if (item.type === 'product') goTo('/produtos');
      else if (item.type === 'order') goTo('/encomendas');
    }
  };

  const hasResults = !!(results && (results.customers?.length || results.products?.length || results.orders?.length));
  const totals = results?.totals || {};

  let globalIdx = -1;
  const nextIdx = () => { globalIdx++; return globalIdx; };

  return (
    <>
    {drawerProduct && createPortal(
      <ProductDetailDrawer
        product={drawerProduct}
        categoryMap={categoryMap}
        open={!!drawerProduct}
        onClose={() => setDrawerProduct(null)}
        clientView={false}
        onAddToCart={(product, quantity, variant) =>
          navigate('/produtos', { state: { addToCart: { productId: product.id, variantId: variant?.id ?? null, quantity } } })}
      />,
      document.body
    )}
    <div className="relative flex-1 max-w-lg" ref={ref} data-testid="global-search">
      {/* Input */}
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
      <input
        ref={inputRef}
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => results && setOpen(true)}
        onKeyDown={handleKey}
        placeholder="Pesquisar clientes, produtos, encomendas..."
        className="w-full pl-10 pr-16 h-9 text-sm bg-slate-900/60 border border-slate-700 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/20 transition-all"
        data-testid="global-search-input"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
        {query ? (
          <button onClick={() => { setQuery(''); setOpen(false); }} className="text-slate-500 hover:text-slate-300 transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <kbd className="hidden sm:flex items-center gap-0.5 text-[10px] text-slate-600 border border-slate-700 rounded px-1 py-0.5 font-mono">
            ⌘K
          </kbd>
        )}
      </div>

      {/* Dropdown */}
      {open && query.length >= 2 && (
        <div
          className="absolute top-full left-0 mt-1.5 bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl shadow-black/40 z-[200] overflow-hidden"
          style={{ width: 'max(100%, 480px)' }}
          data-testid="search-results"
        >
          {/* Loading */}
          {loading && (
            <div className="p-5 flex items-center justify-center gap-2 text-sm text-slate-500">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              A pesquisar...
            </div>
          )}

          {/* Empty */}
          {!loading && !hasResults && (
            <div className="p-6 text-center">
              <Search className="w-8 h-8 text-slate-700 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Sem resultados para <span className="text-slate-300">"{query}"</span></p>
            </div>
          )}

          {!loading && hasResults && (
            <div className="py-1.5 max-h-[420px] overflow-y-auto">

              {/* Clientes */}
              {results.customers?.length > 0 && (
                <section>
                  <div className="flex items-center justify-between px-3 py-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                      <Users className="w-3 h-3" /> Clientes
                      <span className="text-slate-700 font-normal normal-case tracking-normal">
                        {totals.customers > results.customers.length ? `· mostrando ${results.customers.length} de ${totals.customers}` : `· ${totals.customers}`}
                      </span>
                    </span>
                  </div>
                  {results.customers.map(c => {
                    const idx = nextIdx();
                    return (
                      <button
                        key={c.id}
                        onClick={() => goTo(`/clientes/${c.id}`)}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${active === idx ? 'bg-blue-500/10' : 'hover:bg-slate-800/60'}`}
                        data-testid={`search-customer-${c.id}`}
                      >
                        <Avatar name={c.name} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-slate-200 font-medium truncate">
                            <Highlight text={c.name} query={query} />
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                            {c.locality && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{c.locality}</span>}
                            {c.email && <span className="truncate">{c.email}</span>}
                            {!c.email && c.phone && <span>{c.phone}</span>}
                          </p>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                      </button>
                    );
                  })}
                  {totals.customers > results.customers.length && (
                    <button onClick={() => goTo('/clientes')} className="w-full px-3 py-1.5 text-left text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors">
                      <ArrowRight className="w-3 h-3" /> Ver todos os {totals.customers} clientes
                    </button>
                  )}
                  <div className="mx-3 border-t border-slate-800 my-1" />
                </section>
              )}

              {/* Produtos */}
              {results.products?.length > 0 && (
                <section>
                  <div className="flex items-center px-3 py-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                      <Package className="w-3 h-3" /> Produtos
                      <span className="text-slate-700 font-normal normal-case tracking-normal">
                        {totals.products > results.products.length ? `· mostrando ${results.products.length} de ${totals.products}` : `· ${totals.products}`}
                      </span>
                    </span>
                  </div>
                  {results.products.map(p => {
                    const idx = nextIdx();
                    return (
                      <button
                        key={p.id}
                        onClick={() => openProduct(p)}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${active === idx ? 'bg-blue-500/10' : 'hover:bg-slate-800/60'}`}
                        data-testid={`search-product-${p.id}`}
                      >
                        {p.photo_url ? (
                          <img src={fileUrl(p.photo_url, 160)} alt="" className="w-8 h-8 rounded-lg object-cover bg-slate-800 flex-shrink-0 border border-slate-700" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0">
                            <Package className="w-3.5 h-3.5 text-slate-600" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-slate-200 font-medium truncate">
                            <Highlight text={p.name} query={query} />
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                            {p.reference && <span className="flex items-center gap-0.5"><Hash className="w-2.5 h-2.5" /><Highlight text={p.reference} query={query} /></span>}
                            {p.category && <span className="text-slate-600">{p.category}</span>}
                          </p>
                        </div>
                        {p.selling_price > 0 && (
                          <span className="text-sm font-semibold text-blue-400 flex-shrink-0">{formatCurrency(p.selling_price)}</span>
                        )}
                      </button>
                    );
                  })}
                  {totals.products > results.products.length && (
                    <button onClick={() => goTo('/produtos')} className="w-full px-3 py-1.5 text-left text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors">
                      <ArrowRight className="w-3 h-3" /> Ver todos os {totals.products} produtos
                    </button>
                  )}
                  <div className="mx-3 border-t border-slate-800 my-1" />
                </section>
              )}

              {/* Encomendas */}
              {results.orders?.length > 0 && (
                <section>
                  <div className="flex items-center px-3 py-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                      <ShoppingCart className="w-3 h-3" /> Encomendas
                      <span className="text-slate-700 font-normal normal-case tracking-normal">
                        {totals.orders > results.orders.length ? `· mostrando ${results.orders.length} de ${totals.orders}` : `· ${totals.orders}`}
                      </span>
                    </span>
                  </div>
                  {results.orders.map(o => {
                    const idx  = nextIdx();
                    const st   = STATUS_LABEL[o.status] || { label: o.status, color: 'text-slate-400 bg-slate-400/10' };
                    const date = o.order_date ? new Date(o.order_date).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' }) : '';
                    return (
                      <button
                        key={o.id}
                        onClick={() => { navigate('/encomendas', { state: { filterCustomerId: o.customer_id } }); setQuery(''); setOpen(false); }}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${active === idx ? 'bg-blue-500/10' : 'hover:bg-slate-800/60'}`}
                        data-testid={`search-order-${o.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0">
                          <ShoppingCart className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-slate-200 font-medium truncate">
                            <Highlight text={o.customer_name} query={query} />
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                            <span className="font-mono">{o.id?.slice(0, 8)}</span>
                            {date && <span>{date}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                          <span className="text-sm font-semibold text-emerald-400">{formatCurrency(o.total_revenue)}</span>
                        </div>
                      </button>
                    );
                  })}
                  {totals.orders > results.orders.length && (
                    <button onClick={() => goTo('/encomendas')} className="w-full px-3 py-1.5 text-left text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors">
                      <ArrowRight className="w-3 h-3" /> Ver todas as {totals.orders} encomendas
                    </button>
                  )}
                </section>
              )}

              {/* Rodapé com atalho */}
              <div className="px-3 pt-1 pb-2 flex items-center gap-3 text-[10px] text-slate-700 border-t border-slate-800 mt-1">
                <span><kbd className="border border-slate-700 rounded px-1">↑↓</kbd> navegar</span>
                <span><kbd className="border border-slate-700 rounded px-1">Enter</kbd> abrir</span>
                <span><kbd className="border border-slate-700 rounded px-1">Esc</kbd> fechar</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default GlobalSearch;
